import React, { useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Paper,
  Box,
} from "@mui/material";
import { CircularProgressCustom } from "../../utils/muiCustomTheme";

interface ExecutionLogTableProps {
  data?: any;
  isLoading?: boolean;
}

const ExecutionLogTable: React.FC<ExecutionLogTableProps> = ({ data, isLoading }) => {
  const rows: any[] = Array.isArray(data) ? data : [];

  // Take last (newest timestamp) row per job_id
  const lastPerJob = useMemo(() => {
    const map = new Map<string, any>();
    for (const row of rows) {
      const jid = row.job_id || "";
      const existing = map.get(jid);
      if (!existing || (row.timestamp || "") > (existing.timestamp || "")) {
        map.set(jid, row);
      }
    }
    return Array.from(map.values()).sort(
      (a, b) => (b.timestamp || "").localeCompare(a.timestamp || "")
    );
  }, [rows]);

  const allColumns = [
    { field: "job_id", headerName: "Job ID" },
    { field: "stage", headerName: "Stage" },
    { field: "destinations", headerName: "Destinations" },
    { field: "destinations_counts", headerName: "Destination Counts" },
    { field: "success_rate", headerName: "Success Rate" },
    { field: "error_codes", headerName: "Error Codes" },
    { field: "error_messages", headerName: "Error Messages" },
    { field: "timestamp", headerName: "Timestamp" },
  ];

  const hasData = (field: string) =>
    lastPerJob.some((row) => {
      const v = row[field];
      return Array.isArray(v) ? v.length > 0 : v != null && v !== "";
    });

  const columns = allColumns.filter(
    (col) => !["error_codes", "error_messages"].includes(col.field) || hasData(col.field)
  );

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
        <CircularProgressCustom />
      </Box>
    );
  }

  if (!lastPerJob.length) {
    return (
      <div className="create-campaign__delivery-preview__status">
        No execution log found for this campaign
      </div>
    );
  }

  const formatCell = (row: any, field: string) => {
    const val = row[field];
    if (field === "timestamp" && val) {
      return new Date(val).toLocaleString("en-US", {
        month: "short",
        day: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    }
    if (field === "destinations_counts" && Array.isArray(val)) {
      return val.map((item: string, i: number) => {
        const colonIdx = item.indexOf(":");
        if (colonIdx > -1) {
          return (
            <div key={i}>
              <strong>{item.slice(0, colonIdx)}</strong>{item.slice(colonIdx)}
            </div>
          );
        }
        return <div key={i}>{item}</div>;
      });
    }
    if (Array.isArray(val)) return val.join(", ");
    return val ?? "-";
  };

  return (
    <Box>
      <Box sx={{ mb: 1, fontSize: "0.85rem", color: "#666" }}>
        Showing last 20 executions
      </Box>
    <TableContainer component={Paper} sx={{ width: "100%" }}>
      <Table sx={{ tableLayout: "fixed", width: "100%" }}>
        <TableHead>
          <TableRow>
            {columns.map((col) => (
              <TableCell key={col.field} sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
                {col.headerName}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {lastPerJob.map((row: any, index: number) => (
            <TableRow
              key={index}
              sx={{ "&:hover": { backgroundColor: "#FFEBF5" } }}
            >
              {columns.map((col) => {
                const text = formatCell(row, col.field);
                return (
                  <TableCell
                    key={col.field}
                    title={typeof text === "string" ? text : undefined}
                    sx={{
                        maxWidth: col.field === "destinations_counts" ? 320 : 200,
                      overflow: "hidden",
                      textOverflow: col.field === "destinations_counts" ? undefined : "ellipsis",
                      whiteSpace: col.field === "destinations_counts" ? "normal" : "nowrap",
                    }}
                  >
                    {text}
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
    </Box>
  );
};

export default ExecutionLogTable;
